/**
 * PhotoLightbox.tsx — the ONE in-portal viewer for a patient's weight photos.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: clinical review / CRM pipeline.
 *
 * ⚠️ WHY THIS EXISTS: A SIGNED URL MUST NEVER REACH THE ADDRESS BAR
 * (CTO, 2026-09-02: "if i get the image URL i can open it in any browser, no
 * session is needed... so URL is not exposed on URL bar").
 *
 * The admin gallery used to open the full-size photo with
 * `<a href={signedUrl} target="_blank">`. That is worse than it looks. A V4
 * signed READ url is a BEARER CAPABILITY: possession alone dereferences to a
 * photograph of a named patient standing on scales — no session, no role, no
 * tenant check, for as long as the signature is valid. Putting it in `href`
 * meant it landed in the address bar, in browser history, in the "Copy link
 * address" context menu, and in anything reading `document.referrer` on the
 * next navigation. Article 9 health data, one right-click from a shareable
 * link that outlives the staff member's own session.
 *
 * Rendering the bytes in an `<img>` inside a dialog leaks none of that: the url
 * stays a JS value and an attribute the browser never navigates to. It is not a
 * secret — the DOM still holds it — but it is no longer a URL the person
 * looking at the screen can casually pass on.
 *
 * ⚠️ AND THE EARLIER REASONING FOR THE NEW TAB WAS WRONG. `SubmittedPhotos`
 * justified it as avoiding "stacking a second modal over the drawer, trapping
 * keyboard focus". `DialogShell` already solves exactly that — role, aria-modal,
 * Escape and focus-return — and the CLINICIAN gallery had been stacking it over
 * the same drawer since 2026-08-27. The pattern to reuse was already there.
 *
 * ONE VIEWER, BOTH SURFACES. The clinician's `WeightPhotoSection` and the
 * admin's `SubmittedPhotos` both mount this, so the two staff consoles cannot
 * drift into showing the same evidence two different ways — and the navigation
 * below only had to be built once.
 *
 * ⚠️ THE WHOLE PHOTO IS ALWAYS VISIBLE (2026-09-06). The image was
 * `width: 100%; height: auto` with NO cap, so a portrait shot off a phone —
 * which is most of them, at 3:4 or 9:16 — rendered ~1000px tall inside a 760px
 * panel and the reader SCROLLED THE MODAL to see the scale readout. A clinician
 * comparing two photos cannot compare what is below the fold. So the image is
 * bounded to the viewport and `object-fit: contain` letterboxes it inside that
 * bound: the height clamp alone would SQUASH the picture (width stays 100% while
 * the box gets shorter), and a squashed body is a distorted clinical
 * observation, not a cosmetic defect.
 *
 * NAVIGATION, because the point is comparing (CTO: "clinicians can switch from
 * one image to the next one by one on a modal"). Patients upload several angles
 * — the scale readout, a full-body shot — and the decision is made by looking
 * across them. Closing and reopening the dialog per photo made that a chore, so
 * Previous/Next move within the open dialog and the arrow keys do the same.
 * The controls DISABLE at each end rather than wrapping: a set of three is
 * small enough that silently looping back to the first reads as a bug, and a
 * clinician needs to know they have seen all of them.
 *
 * ⚠️ THE PAGER SITS ON THE IMAGE (2026-09-06), and it kept its names. It used
 * to be two ghost buttons in a footer row BELOW the provenance line, which put
 * the control for "show me the next photo" further from the photo than the
 * sentence describing it — and, once the image was bounded to the viewport
 * above, off the bottom of a short laptop screen entirely. Overlaid arrows sit
 * where the eye already is. The accessible names are still exactly `Previous`
 * and `Next` (aria-label; the chevron is decorative) and the arrow-key handling
 * is unchanged, because those are what the two consoles' specs and every
 * keyboard user already know.
 *
 * ⚠️ ONE THING THE OVERLAY MUST BRING ITSELF IS CONTRAST. Every other control
 * in the estate can inherit its colours from `--gps-surface`; these float over
 * a photograph we have never seen — a white scale face, a dark bathroom floor,
 * a window blowing out the exposure. A token that passes AA on the page says
 * nothing about that. So `overlayControl` carries its own dark ground under a
 * white glyph, and the contrast becomes a property of the button rather than a
 * bet on the picture.
 *
 * ⚠️ PROVENANCE (2026-09-05, CTO: the viewer "must say who and when").
 *
 * WHO it renders, because the read carries it: `AssessmentPhotoEntry.addedByRole`
 * is a role enum written at grant time (`PATIENT` | `CLINICIAN` | `ADMIN`), and
 * under weight self-certification it is the EVIDENTIAL CLAIM, not bookkeeping —
 * "the patient supplied this" and "a clinician obtained it themselves" are
 * different kinds of proof, and a prescriber weighing a declaration against an
 * image has to know which one is in front of them. A ROLE, never a name (R19).
 *
 * ⚠️ WHEN IS NOT RENDERED, BECAUSE IT IS NOT ON THE WIRE, AND NOTHING ADJACENT
 * IS SUBSTITUTED FOR IT. `assessmentPhotosRead` sends no timestamp. It DOES send
 * `remainingDays` — whole days left before the 365-day retention sweep — and
 * `365 - remainingDays` looks like an upload date. It is not one: it is a
 * RETENTION countdown, server-clamped at 0, and it would silently start lying
 * the day the retention window is changed or made per-photo. A plausible wrong
 * date on a clinical record is worse than no date, so the viewer says the date
 * is unavailable and claims nothing.
 *
 * TO FIX IT PROPERLY, clinic-service has the value already: `photosRead()`
 * reads `AssessmentPhoto.createdAt` into `storedAtByRef`, spends it on
 * `remainingDays` and drops it. Adding `addedAt` (ISO UTC) to
 * `assessmentPhotosRead` is the whole change; this viewer then renders it and
 * the "not available" clause goes.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ THE ⋯ MENU, AND WHY THE VIEWER NEVER SHOWS THE RESULT OF WHAT IT STARTS.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The retraction — the one control in the portal that destroys Article 9
 * clinical evidence permanently — moved here from a per-tile "Remove" button
 * under every thumbnail (CTO, 2026-09-06: it is the rarest action on the card
 * and it was the most prominent, sitting at rest under every image on a
 * clinical record). One level down, behind ⋯, on the photo you are actually
 * looking at.
 *
 * The viewer OFFERS it and owns nothing else about it:
 *
 *  - WHO SEES IT is the caller's call, and the caller matches the server's
 *    guard on `DELETE /v1/clinic/assessments/:id/photos/:photoId` —
 *    `@Roles('ADMIN', 'CLINICIAN')` since 2026-09-06 (a CTO decision over a
 *    raised objection; both sides are recorded in `ReviewView`'s block above
 *    `photoGrid`). ⚠️ This line said `@Roles('ADMIN')` for part of that day,
 *    which is exactly the kind of second copy the rule below warns about: this
 *    component has no role knowledge and must not grow any — R41, the server is
 *    the control, and a restatement here is one more thing to keep in step with
 *    it, as this sentence just demonstrated by falling behind.
 *  - ⚠️ A PHOTO WITH NO `photoId` GETS NO MENU AT ALL. The delete is addressed
 *    by id and never by tile position (positions shift under every upload and
 *    every retention sweep); a legacy `Assessment.photoRef` pointer has no
 *    ledger row and therefore no id, so there is nothing to aim at. A control
 *    that cannot aim is worse than no control, and an empty ⋯ is worse than no
 *    ⋯, so the trigger itself is absent — not a disabled row.
 *  - ⚠️ THE OUTCOME AND THE FAILURE ARE THE CARD'S, NOT THE VIEWER'S. Selecting
 *    the row hands the id back and the caller closes this dialog. That is
 *    deliberate and load-bearing: the messages that follow ("Photo removed —
 *    there is now no weight evidence on file", or a 503's "nothing was
 *    removed") describe the surface UNDERNEATH a fixed overlay, and retracting
 *    the last photo unmounts the grid a message would otherwise sit in. Nothing
 *    about the result of a retraction may ever be rendered in here.
 */
import { useCallback, useEffect, type CSSProperties } from 'react';
import { Button } from '../ui/kit';
import { DialogShell } from '../ui/ConfirmDialog';
import { Menu, MenuItem } from '../ui/Menu';

/** The alt text a single tile and the open dialog share, so they cannot disagree. */
export function photoAlt(index: number, total: number): string {
  return total === 1
    ? "Patient's submitted weight photo"
    : `Patient's submitted weight photo ${index + 1} of ${total}`;
}

const navButton = { width: 'auto', minHeight: '44px', padding: '10px 18px' } as const;

/**
 * The shared chrome of a control that sits ON the photograph — the two pager
 * arrows and the ⋯ trigger. 44px square (WCAG 2.5.8), and it brings its own
 * ground rather than inheriting one from a picture we cannot see (see header).
 */
const overlayControl: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '44px',
  height: '44px',
  padding: 0,
  border: '1px solid rgba(255, 255, 255, 0.55)',
  borderRadius: 'var(--gps-r-pill)',
  background: 'rgba(19, 35, 27, 0.78)',
  color: '#ffffff',
  font: 'inherit',
  lineHeight: 1,
  cursor: 'pointer',
};

/** A pager chevron. Decorative — the button's aria-label carries the name. */
function Chevron({ direction }: { direction: 'prev' | 'next' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={direction === 'prev' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'} />
    </svg>
  );
}

/**
 * One overlaid pager arrow, vertically centred on the image edge.
 *
 * ⚠️ THE NAME IS THE OLD ONE, EXACTLY. `Previous`/`Next` is what both consoles'
 * specs query and what a screen-reader user who has used this viewer before
 * expects; a chevron that renamed itself "Go back" while looking the same would
 * be a silent breaking change to everyone not looking at the screen. `title`
 * repeats it for the sighted mouse user who now has no visible word.
 *
 * DISABLED AT THE ENDS RATHER THAN HIDDEN, keeping the pre-overlay behaviour:
 * a control that vanishes tells you nothing, and a dimmed one says "this is the
 * first photo" (which is the fact the clinician needs — see the header).
 */
function NavArrow({
  direction,
  disabled,
  onClick,
}: {
  direction: 'prev' | 'next';
  disabled: boolean;
  onClick: () => void;
}) {
  const label = direction === 'prev' ? 'Previous' : 'Next';
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      style={{
        ...overlayControl,
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        ...(direction === 'prev' ? { left: '8px' } : { right: '8px' }),
        ...(disabled ? { opacity: 0.4, cursor: 'default' } : {}),
      }}
    >
      <Chevron direction={direction} />
    </button>
  );
}

/**
 * One photo as the viewer needs it: the live url (null once the caller has
 * marked it expired), WHO produced it, and the server's id for it.
 *
 * ⚠️ ONE OBJECT PER PHOTO, NOT PARALLEL ARRAYS. The url, the role and the id
 * have to stay on the same photo through Previous/Next, and a second array
 * indexed by the same number is one filter away from attributing photo 2's
 * uploader — or, far worse now, photo 2's DELETE ADDRESS — to photo 3. Both
 * callers map their `AssessmentPhotoEntry[]` straight into this.
 */
export interface PhotoLightboxItem {
  /** The signed read url, or null where there is no live preview. */
  url: string | null;
  /**
   * The role that produced the image, `null`/absent when the read does not say
   * (a legacy `photoRef` pointer, or a grant predating the column). Absent is
   * NEVER read as "the patient".
   */
  addedByRole?: 'PATIENT' | 'CLINICIAN' | 'ADMIN' | null;
  /**
   * The server's id for this photo — the ONLY thing a retraction may be
   * addressed by. `null`/absent for a legacy `Assessment.photoRef` pointer with
   * no ledger row behind it, and that absence removes the ⋯ menu entirely
   * rather than aiming the delete at a guess (see the header).
   */
  photoId?: string | null;
}

/**
 * The provenance line under the image — WHO, and the honest admission that the
 * WHEN is not on this read (see the header).
 *
 * The unknown branch says so in words rather than rendering nothing: a silent
 * line reads as "the patient sent it", which is the default assumption a
 * clinician makes about a weight photo and the one that must not be made for
 * them.
 */
export function photoProvenance(addedByRole: PhotoLightboxItem['addedByRole']): string {
  const who =
    addedByRole === 'PATIENT'
      ? 'Added by the patient'
      : addedByRole === 'CLINICIAN'
        ? 'Added by a clinician'
        : addedByRole === 'ADMIN'
          ? 'Added by an admin'
          : 'Not recorded who added this';
  return `${who} · date added not available`;
}

export function PhotoLightbox({
  photos,
  index,
  onIndex,
  onClose,
  onExpired,
  onRetract,
}: {
  /** The photos, in the order the tiles show them — url + who added it + its id. */
  photos: readonly PhotoLightboxItem[];
  index: number;
  onIndex: (next: number) => void;
  onClose: () => void;
  /** The url died while open — the caller marks that tile expired and offers its reload. */
  onExpired: (index: number) => void;
  /**
   * Offer the ⋯ → remove row for a photo the server gave an id. OMITTED (not
   * `false`) for anyone who may not retract — the caller matches the server's
   * `@Roles('ADMIN', 'CLINICIAN')` (widened from ADMIN-only on 2026-09-06; see
   * the header), and this component deliberately holds no role knowledge of its
   * own (R41).
   *
   * ⚠️ THE CALLER MUST CLOSE THE VIEWER on this callback: the confirm, the
   * outcome and every failure belong on the card behind it (see the header).
   */
  onRetract?: (photoId: string, index: number) => void;
}) {
  const total = photos.length;
  const photo = photos[index];
  const url = photo?.url ?? null;
  const hasPrev = index > 0;
  const hasNext = index < total - 1;
  /** The id this photo's ⋯ row would aim at — `null` kills the whole menu. */
  const retractableId = onRetract && photo?.photoId ? photo.photoId : null;

  const go = useCallback(
    (delta: number) => {
      const next = index + delta;
      if (next >= 0 && next < total) onIndex(next);
    },
    [index, total, onIndex],
  );

  /* Arrow keys, because a viewer you step through is a viewer you step through
     with the keyboard. Escape is deliberately NOT handled here — DialogShell
     owns it, and a second listener would close the dialog twice.

     ⚠️ THE GUARD IS NEW WITH THE ⋯ MENU (2026-09-06). This listener is on the
     DOCUMENT, so it fires wherever focus is — including on a row of the open
     menu. Stepping the photo from under an open menu re-labels the row the
     reader is sitting on ("Remove photo 2 of 3" becomes "…3 of 3") between
     hearing it and pressing it, which is the one thing a destructive row must
     never do. The composing/typing targets are in the same list for the same
     reason: `preventDefault` on a document listener would otherwise pin the
     caret in any field a future version of this dialog grows. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest?.('[role="menu"], input, textarea, select, [contenteditable="true"]')) return;
      if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
      else return;
      e.preventDefault();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [go]);

  /* A url that expires WHILE the dialog is open would leave a broken image
     filling it. Close back to the grid instead and let the tile carry the
     expiry note beside its Reload control — one explanation, in the one place
     that also offers the way out. */
  if (url === null) {
    return null;
  }

  return (
    <DialogShell
      title={total === 1 ? 'Weight photo' : `Weight photo ${index + 1} of ${total}`}
      width="min(760px, 100%)"
      onCancel={onClose}
    >
      {/* The image and the controls that ride on it. `position: relative` is
          the only reason the overlays land on the picture rather than in the
          panel's corner — do not flatten this wrapper away. */}
      <div style={{ position: 'relative' }}>
        <img
          src={url}
          alt={photoAlt(index, total)}
          onError={() => {
            onExpired(index);
            onClose();
          }}
          style={{
            display: 'block',
            width: '100%',
            height: 'auto',
            /* ⚠️ BOTH HALVES, OR NEITHER — see the header. The cap keeps a
               portrait phone shot inside the viewport instead of making the
               reader scroll the modal past the part they came to read; the
               `contain` stops that same cap from squashing the picture, which
               would distort a clinical observation rather than crop it. */
            maxHeight: '68vh',
            objectFit: 'contain',
            borderRadius: 'var(--gps-r-md)',
            border: '1px solid var(--gps-border)',
            /* The letterbox `contain` leaves beside a very tall or very wide
               photo — the page ground, so it reads as empty frame and never as
               part of the image. */
            background: 'var(--gps-bg)',
          }}
        />
        {total > 1 ? (
          <>
            <NavArrow direction="prev" disabled={!hasPrev} onClick={() => go(-1)} />
            <NavArrow direction="next" disabled={!hasNext} onClick={() => go(1)} />
          </>
        ) : null}
        {/* ⋯ — present ONLY when this photo has both a permitted action and a
            server id to aim it at (see the header). Top-right, away from both
            pager arrows: the two neighbouring controls here are "show me the
            next photo" and "destroy this one". */}
        {retractableId ? (
          <Menu
            menuLabel="Photo actions"
            triggerLabel="Photo actions"
            triggerStyle={overlayControl}
            wrapperStyle={{ position: 'absolute', top: '8px', right: '8px' }}
          >
            {({ close }) => (
              <MenuItem
                tone="danger"
                onSelect={() => {
                  // Close FIRST so focus lands back on a trigger that still
                  // exists; the caller's handler then unmounts this dialog.
                  close();
                  onRetract?.(retractableId, index);
                }}
              >
                {/* The row names WHICH photo when there is more than one. The
                    confirm that follows repeats the ordinal in its title —
                    this is the last point at which the reader can still see
                    the image the sentence is about. */}
                {total === 1 ? 'Remove this photo' : `Remove photo ${index + 1} of ${total}`}
              </MenuItem>
            )}
          </Menu>
        ) : null}
      </div>
      {/* PROVENANCE, directly under the image it describes — see the header for
          why the role is rendered and the date is not. Muted and small: it is
          context for the picture, not a competing headline. */}
      <p style={{ margin: 'var(--gps-s-2) 0 0', fontSize: '13px', color: 'var(--gps-muted)', lineHeight: 1.45 }}>
        {photoProvenance(photo?.addedByRole)}
      </p>
      <div style={{ display: 'flex', gap: 'var(--gps-s-2)', marginTop: 'var(--gps-s-3)', flexWrap: 'wrap' }}>
        {/* Close keeps its word and its place in the footer. Escape and the
            backdrop already close the dialog; this is the one a touch user
            without a keyboard reaches for, and an icon-only ✕ floating on the
            photo would put "leave" next to "destroy". */}
        <Button type="button" variant="ghost" onClick={onClose} style={navButton}>
          Close
        </Button>
      </div>
    </DialogShell>
  );
}
